import { useEffect, useRef, useState } from "react";
import type { RdScheduleActionOptionsResponse } from "../../../../../shared/rd-schedule-actions";
import { fetchActionOptions, type ScheduleApi } from "../../../lib/rd-schedule-actions";
import { NOTE_POLL_EVERY_MS, NOTE_POLL_FOR_MS } from "../../../lib/rd-schedule-notes";

export type ActionOptions = RdScheduleActionOptionsResponse["data"];

export type ActionOptionsState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; options: ActionOptions };

const LOADING: ActionOptionsState = { status: "loading" };

/** Reads the options again every few seconds for a while, handing each good read to `onRead`. */
function useOptionsPolling(
  api: ScheduleApi,
  rdId: string | null,
  appointmentId: string,
  active: boolean,
  pollForMs: number,
  onRead: (options: ActionOptions) => void
): void {
  const onReadRef = useRef(onRead);

  onReadRef.current = onRead;

  useEffect(() => {
    if (!active) return undefined;

    let current = true;
    const startedAt = Date.now();
    const timer = setInterval(() => {
      if (Date.now() - startedAt > pollForMs) {
        clearInterval(timer);

        return;
      }

      // A quiet read: the panel keeps what it shows, and a failed one is skipped.
      fetchActionOptions(api, rdId, appointmentId)
        .then((response) => {
          if (current) onReadRef.current(response.data);
        })
        .catch(() => undefined);
    }, NOTE_POLL_EVERY_MS);

    return () => {
      current = false;
      clearInterval(timer);
    };
  }, [api, rdId, appointmentId, active, pollForMs]);
}

/** NO-876. The visit's options (outcomes, fee defaults, series, current notes), read when a dialog opens. */
export function useActionOptions(
  api: ScheduleApi,
  rdId: string | null,
  appointmentId: string,
  /** False reads nothing (the details panel for a visit with no note to show). */
  enabled = true,
  /** True reads again every few seconds for a while, so a save Healthie has not caught up with shows. */
  polling = false,
  /** How long polling lasts; a dialog that stays locked for a whole pending window passes that window. */
  pollForMs = NOTE_POLL_FOR_MS
): { state: ActionOptionsState; reload: () => void } {
  // The result is kept with the visit it belongs to, so another visit never shows it.
  const [result, setResult] = useState<{
    appointmentId: string;
    state: ActionOptionsState;
  } | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setResult(null);

      return undefined;
    }

    let current = true;
    setResult({ appointmentId, state: LOADING });

    fetchActionOptions(api, rdId, appointmentId)
      .then((response) => {
        if (current) {
          setResult({
            appointmentId,
            state: { status: "ready", options: response.data },
          });
        }
      })
      .catch(() => {
        if (current) setResult({ appointmentId, state: { status: "error" } });
      });

    return () => {
      current = false;
    };
  }, [api, rdId, appointmentId, enabled, nonce]);

  useOptionsPolling(
    api,
    rdId,
    appointmentId,
    enabled && polling,
    pollForMs,
    (options) => setResult({ appointmentId, state: { status: "ready", options } })
  );

  const state =
    enabled && result?.appointmentId === appointmentId ? result.state : LOADING;

  return { state, reload: () => setNonce((value) => value + 1) };
}
